import { mostRecentCrawlStartMs } from './crawl-schedule';

/**
 * The "new jobs" filter (`firstSeenMin`) is stored in the URL either as an absolute epoch-ms
 * threshold (what the filter modal's quick filter writes) or as one of these stable relative
 * codes (what shareable links such as the navbar's "new today" figure use, so the URL does not
 * change every hour and crawlers see one address instead of a new one per request). Both forms
 * resolve to the same `firstSeen >= threshold` query at search time, i.e. jobs the crawler first
 * saw within the window. `firstSeen` is the reliable "is this job new?" signal: `postedDate` can
 * be backdated or absent, and `lastSeen` moves on every re-crawl.
 */
export const FIRST_SEEN_RELATIVE_HOURS: Record<string, number> = {
  '6h': 6,
  '24h': 24,
  '3d': 72,
  '1w': 168,
  '2w': 336,
  '1m': 720,
};

/**
 * The stable code behind the navbar's "new today" figure link (TEC-683). This is deliberately a
 * separate code from the public "24h" quick filter above, not an alias for it: the navbar tile's
 * count (`server/utils/nav-pulse.ts`) is anchored to the crawl schedule (`mostRecentCrawlStartMs`),
 * not a rolling 24h window, so its link has to resolve to that same boundary or the number a
 * visitor sees and the number they land on after clicking would disagree. The public "24h" quick
 * filter chip keeps its rolling-window meaning; this code is never offered as a user-facing
 * choice, only ever written into this one link.
 */
export const NEW_TODAY_FIRST_SEEN_CODE = 'crawl-day';

/** Epoch-ms threshold for a stored `firstSeenMin` value, or null when the value is unusable. */
export function resolveFirstSeenThreshold(raw: string | number | undefined | null, now: number = Date.now()): number | null {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const value = String(raw).trim();
  if (value === NEW_TODAY_FIRST_SEEN_CODE) return mostRecentCrawlStartMs(now);
  const hours = FIRST_SEEN_RELATIVE_HOURS[value];
  if (hours) return now - hours * 60 * 60 * 1000;
  const num = Number(value);
  return Number.isFinite(num) && value !== '' ? num : null;
}
